/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, useCallback, useEffect, useRef} from 'react';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import {useSignals} from '@preact/signals-react/runtime';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {GlobalGroupChooserContent} from '@ion/dls/src/components/groupchooser/GlobalGroupChooserContent';
import {
  dlsSidebarDrawerOffset,
  dlsSidebarWidth,
  isSideNavCollapsed
} from '@ion/dls/src/components/navigation/NavigationStore';
import {useSideNavDockedDrawer} from '@ion/dls/src/components/navigation/useSideNavDockedDrawer';
import {GroupChooserIcon} from '@ion/dls/src/icons/GroupChooserIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsShadow, dlsTypography, dlsZIndex} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import SkeletonLoader from '../loading/SkeletonLoader';
import {AutoSelectClient} from './GroupChooserAccordion';

const LOADER_HEIGHT = 16;
const LOADER_WIDTH = 80;

export interface GlobalGroupChooserProps extends AutoSelectClient {
  accountHierarchy: AccountLink[];
  selectedGroup: AccountLink;
  drawerOpen: boolean;
  includeSelectedAccountId?: boolean;
  includeBreadcrumbs?: boolean;
  hideParentAccount?: boolean;
  onDrawerClick: MouseEventHandler<HTMLDivElement>;
  onCloseDrawer: MouseEventHandler<HTMLButtonElement>;
  onSelectGroup: (value: AccountLink) => void;
  anchor?: 'bottom' | 'left' | 'right' | 'top';
  resetDeviceMovedAccount?: Function;
  hasAssetDeviceMove?: boolean;
  allCrumbsClickable?: boolean;
  onlyAccountId?: boolean;
}

const useStyles = makeStyles(theme => ({
  groupChooser : {
    padding      : '8px',
    borderRadius : '4px',
    '&:hover'    : {
      backgroundColor  : dlsColor('paleBlue'),
      backgroundRadius : '50%'
    }
  },
  groupChooserDisabled : {
    pointerEvents : 'none'
  },
  groupChooserButtonLabel : {
    ...dlsTypography('body1'),
    marginLeft : '4px',
    cursor     : 'pointer',
    whiteSpace : 'nowrap'
  },
  drawerRoot : {
    zIndex : `${dlsZIndex('globalGroupChooserDrawerRoot')} !important` as any
  },
  drawerPaper : {
    left                           : ({collapsed}: {collapsed: boolean}) => dlsSidebarDrawerOffset(collapsed),
    right                          : 'auto !important',
    width                          : '624px',
    overflow                       : 'hidden',
    boxShadow                      : dlsShadow(1),
    borderRadius                   : '0 12px 12px 0',
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        width  : '100vw',
        left   : '0px !important',
        height : '100vh'
      }
    }
  },
  chooserIcon : {
    display        : 'flex',
    justifyContent : 'center'
  },
  groupChooserContent : toggleThemeStyle({
    width    : '480px',
    padding  : '8px 18px 18px 36px',
    overflow : 'hidden'
  }, {
    padding : '4px 18px 0 18px'
  }),
  closeIcon : {
    position  : 'absolute',
    marginTop : '5px'
  }
}));

/**
 * Desktop Global Group Chooser that, based on the account Hierarchy
 * passed to it will render a nested accordion with account options and
 * fire the passed callback when applied.
 *
 * ```typescript
 * import {GlobalGroupChooser} from '@ion/dls/src/components/groupchooser/GlobalGroupChooser';
 *
 *   <GlobalGroupChooser accountHierarchy={accountHierarchy}
 *     selectedGroup={globalGroup}
 *     drawerOpen={openChooser}
 *     onDrawerClick={onClick}
 *     onCloseDrawer={onCloseDrawer}
 *     onSelectGroup={selectGroup} />
 * ```
 *
 * @param accountHierarchy accountHierarchy that the accordion list will be built from
 * @param selectedGroup currently selected account/group
 * @param drawerOpen if the chooser drawer should be open or closed
 * @param onDrawerClick fires when drawer button is clicked
 * @param onCloseDrawer fires when drawer button is closed
 * @param onSelectGroup fires when user applies the changes in the form
 * @param resetDeviceMovedAccount fires to reset device moved account
 * @param hasAssetDeviceMove check whether asset device move is available or not
 *
 */
export const GlobalGroupChooser = ({
  accountHierarchy,
  selectedGroup,
  drawerOpen,
  onDrawerClick,
  onCloseDrawer,
  onSelectGroup,
  anchor = 'right',
  onlyAccountId = false,
  hideParentAccount = false,
  includeBreadcrumbs = false,
  resetDeviceMovedAccount,
  includeSelectedAccountId = false,
  hasAssetDeviceMove = false,
  allCrumbsClickable = false,
  disableAutoSelect = false
}: GlobalGroupChooserProps) => {
  useSignals();

  const sideNavCollapsed       = isSideNavCollapsed.value;
  const classes                = useStyles({collapsed: sideNavCollapsed});
  const dockedDrawer           = useSideNavDockedDrawer(dlsSidebarWidth(sideNavCollapsed));
  const groupChooserContentRef = useRef<HTMLDivElement>(null);
  const pendingRefreshRef      = useRef(false);
  const initializedRef         = useRef(false);
  const title                  = EntityLabel.get(selectedGroup);
  const accountName            = selectedGroup && includeSelectedAccountId ? `${title}(${entityId(selectedGroup?.href)})` : title;

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  useEffect(function() {
    pendingRefreshRef.current = true;
  }, [accountHierarchy]);

  useEffect(function() {
    if (drawerOpen && (!initializedRef.current || pendingRefreshRef.current)) {
      GlobalGroupsSearch.setAccounts(accountHierarchy);
      initializedRef.current    = true;
      pendingRefreshRef.current = false;
    }
  }, [drawerOpen, accountHierarchy]);

  const onContentScrollToTop = useCallback(function() {
    groupChooserContentRef.current?.scrollTo({
      top      : 0,
      behavior : 'smooth'
    }) as unknown as void;
  }, []);

  return (
    <>
      <div className={`${classes.groupChooser} ${!accountName && classes.groupChooserDisabled}`} onClick={onDrawerClick}>
        <div className={classes.chooserIcon}>
          <GroupChooserIcon />
        </div>
        <span className={classes.groupChooserButtonLabel}>
          {accountName || <SkeletonLoader width={`${LOADER_WIDTH}px`} height={`${LOADER_HEIGHT}px`} />}
        </span>
      </div>
      <Drawer open={drawerOpen}
        anchor={isCurrentTheme ? 'left' : anchor}
        onClose={onCloseDrawer}
        className={dockedDrawer.className}
        disableEnforceFocus={dockedDrawer.disableEnforceFocus}
        BackdropProps={{className: dockedDrawer.backdropClassName}}
        classes={{
          root : classes.drawerRoot,
          ...(isCurrentTheme && {
            paper : classes.drawerPaper
          })
        }}>
        <div className={classes.groupChooserContent} ref={groupChooserContentRef}>
          <Render.If condition={isLegacyTheme}>
            <div className={classes.closeIcon}>
              <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />}
                onClick={onCloseDrawer} />
            </div>
          </Render.If>
          {drawerOpen ? (
            <GlobalGroupChooserContent groups={accountHierarchy}
              onContentScrollToTop={onContentScrollToTop}
              currentGroup={selectedGroup}
              onGroupApply={onSelectGroup}
              drawerOpen={drawerOpen}
              onCloseDrawer={onCloseDrawer}
              onlyAccountId={onlyAccountId}
              hideParentAccount={hideParentAccount}
              includeBreadcrumbs={includeBreadcrumbs}
              resetDeviceMovedAccount={resetDeviceMovedAccount}
              hasAssetDeviceMove={hasAssetDeviceMove}
              allCrumbsClickable={allCrumbsClickable}
              disableAutoSelect={disableAutoSelect} />
          ) : undefined}
        </div>
      </Drawer>
    </>
  );
};

export default GlobalGroupChooser;
